import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Modal,
  Typography,
  Space,
  Tag,
  Collapse,
  Empty,
  Spin,
  Table,
  message,
} from "antd";
import {
  GET_GEO_ALL,
  HEADERS_EXPORT_BEN,
  POST_LAWSUIT_DETAIL,
} from "../../../API/apiUrls";
import axios from "axios";
import dayjs from "dayjs";

const { Text } = Typography;

const AddressTrDetail = ({ open, close, dataDefault }) => {
  const [loading, setLoading] = useState(false);
  const [loanOldData, setLoanOldData] = useState(null);
  const [geoRawData, setGeoRawData] = useState(null);

  // ✅ โหลด GEO Data
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadGeoData = async () => {
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        });

        if (isMounted) {
          setGeoRawData(res.data);
          console.log("✅ geoRawData loaded");
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          console.error("❌ Error loading geo data:", error);
        }
      }
    };

    loadGeoData();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // ✅ โหลดข้อมูล lawsuit
  useEffect(() => {
    if (!open || !dataDefault) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          POST_LAWSUIT_DETAIL,
          {
            CONTNO: dataDefault.CONTNO,
            loanId: dataDefault.loanId,
            lawsuitId: dataDefault.lawsuitId,
          },
          {
            headers: HEADERS_EXPORT_BEN,
          },
        );

        if (response?.data?.lawsuit?.loanOld) {
          setLoanOldData(response.data.lawsuit.loanOld);
          console.log("✅ LoanOld data loaded:", response.data.lawsuit.loanOld);
        }
      } catch (error) {
        console.error("❌ Error loading data:", error);
        message.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [open, dataDefault]);

  // ✅ ฟังก์ชันแปลง code เป็นชื่อ
  const getGeoNameFromCode = useCallback(
    (provCode, distCode, subDistCode) => {
      let provName = null;
      let distName = null;
      let subDistName = null;

      if (geoRawData) {
        const province = geoRawData.province?.find(
          (p) => String(p.provCode) === String(provCode),
        );
        provName = province?.provDesc || null;

        if (province && distCode) {
          const district = geoRawData.district?.find(
            (d) =>
              String(d.provCode) === String(provCode) &&
              String(d.distCode) === String(distCode),
          );
          distName = district?.distDesc || null;
        }

        if (province && distCode && subDistCode) {
          const subDistrict = geoRawData.subDistrict?.find(
            (s) =>
              String(s.provCode) === String(provCode) &&
              String(s.distCode) === String(distCode) &&
              String(s.subDistCode) === String(subDistCode),
          );
          subDistName = subDistrict?.subDistDesc || null;
        }
      }

      return { provName, distName, subDistName };
    },
    [geoRawData],
  );

  // ✅ Table columns สำหรับที่อยู่ ทร.14
  const addressColumns = useMemo(
    () => [
      {
        title: "เลขทะเบียนบ้าน",
        dataIndex: "houseRegistrationNumber",
        key: "houseRegistrationNumber",
        width: 150,
        render: (text) => <Text>{text || "-"}</Text>,
      },
      {
        title: "ที่อยู่",
        key: "fullAddress",
        render: (_, record) => {
          const fullAddress = [
            record.houseNumber,
            record.road && `ถนน ${record.road}`,
            record.soi && `ซอย ${record.soi}`,
            record.village && `หมู่บ้าน ${record.village}`,
          ]
            .filter(Boolean)
            .join(" ");
          return <Text>{fullAddress || "-"}</Text>;
        },
      },
      {
        title: "ตำบล",
        dataIndex: "subdistrict",
        key: "subdistrict",
        width: 120,
        render: (code, record) => {
          const geo = getGeoNameFromCode(
            record.province,
            record.district,
            code,
          );
          return <Text>{geo.subDistName || code || "-"}</Text>;
        },
      },
      {
        title: "อำเภอ",
        dataIndex: "district",
        key: "district",
        width: 120,
        render: (code, record) => {
          const geo = getGeoNameFromCode(record.province, code);
          return <Text>{geo.distName || code || "-"}</Text>;
        },
      },
      {
        title: "จังหวัด",
        dataIndex: "province",
        key: "province",
        width: 120,
        render: (code) => {
          const geo = getGeoNameFromCode(code);
          return <Text>{geo.provName || code || "-"}</Text>;
        },
      },
      {
        title: "รหัสไปรษณีย์",
        dataIndex: "zipcode",
        key: "zipcode",
        width: 100,
        align: "center",
      },
      {
        title: "สร้างเมื่อ",
        dataIndex: "CreatedAt",
        key: "CreatedAt",
        width: 120,
        align: "center",
        render: (date) => {
          const isRecent = dayjs(date).isAfter(dayjs().subtract(30, "day"));
          return (
            <Tag color={isRecent ? "green" : "red"}>
              {dayjs(date).add(543, "year").format("D MMM YY")}
            </Tag>
          );
        },
      },
    ],
    [getGeoNameFromCode],
  );

  // ✅ สร้าง Collapse items
  const collapseItems = useMemo(() => {
    if (!loanOldData) return [];

    const items = [];

    // 1. ผู้เช่าซื้อ
    if (loanOldData.CUSTOMER_ID) {
      const hasAddress =
        loanOldData.addressTr && loanOldData.addressTr.length > 0;

      items.push({
        key: "customer",
        label: (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Space>
              <Tag color="blue" style={{ fontSize: 12 }}>
                <strong>ผู้เช่าซื้อ</strong>
              </Tag>
            </Space>
            <Tag color={hasAddress ? "green" : "red"} style={{ fontSize: 11 }}>
              {hasAddress
                ? `ที่อยู่ ทร.14: ${loanOldData.addressTr.length} รายการ`
                : "ไม่มีที่อยู่ ทร.14"}
            </Tag>
          </Space>
        ),
        children: hasAddress ? (
          <Table
            dataSource={loanOldData.addressTr}
            columns={addressColumns}
            rowKey="addrTrId"
            pagination={false}
            size="small"
            bordered
            scroll={{ x: 1000 }}
          />
        ) : (
          <Empty
            description="ยังไม่มีข้อมูลที่อยู่ ทร.14"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ),
      });
    }

    // 2. ผู้ค้ำ
    if (loanOldData.guarantors && loanOldData.guarantors.length > 0) {
      loanOldData.guarantors.forEach((guarantor, index) => {
        const hasAddress =
          guarantor.addressTr && guarantor.addressTr.length > 0;

        items.push({
          key: `guarantor_${index}`,
          label: (
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Space>
                <Tag color="green" style={{ fontSize: 12 }}>
                  <strong>ผู้ค้ำ {guarantor.garno}</strong>
                </Tag>
                <Tag color="orange" style={{ fontSize: 12 }}>
                  {guarantor.RELATN}
                </Tag>
              </Space>
              <Tag
                color={hasAddress ? "green" : "red"}
                style={{ fontSize: 11 }}
              >
                {hasAddress
                  ? `ที่อยู่ ทร.14: ${guarantor.addressTr.length} รายการ`
                  : "ไม่มีที่อยู่ ทร.14"}
              </Tag>
            </Space>
          ),
          children: hasAddress ? (
            <Table
              dataSource={guarantor.addressTr}
              columns={addressColumns}
              rowKey="addrTrId"
              pagination={false}
              size="small"
              bordered
              scroll={{ x: 1000 }}
            />
          ) : (
            <Empty
              description="ยังไม่มีข้อมูลที่อยู่ ทร.14"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        });
      });
    }

    return items;
  }, [loanOldData, addressColumns]);

  return (
    <Modal
      title={
        <span style={{ fontSize: 17 }}>
          รายละเอียดที่อยู่ ทร.14 - {dataDefault?.CONTNO || ""}
        </span>
      }
      open={open}
      onCancel={close}
      width={1300}
      footer={null}
      destroyOnHidden
    >
      <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
        <Spin spinning={loading} tip="กำลังโหลดข้อมูล...">
          {collapseItems.length > 0 ? (
            <Collapse items={collapseItems} />
          ) : (
            <Empty description="ไม่มีข้อมูลที่อยู่ ทร.14" />
          )}
        </Spin>
      </div>
    </Modal>
  );
};

export default AddressTrDetail;
